import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Search, UserCircle, Calendar, RefreshCw } from "lucide-react";
import WeeklyAvailabilityGrid from "./WeeklyAvailabilityGrid";
import TimeSlotDetailModal from "./TimeSlotDetailModal";
import PrivateAppointmentModal from "./PrivateAppointmentModal";
import { ApiService } from "../../../services/api";
import { AppointmentAvailability } from "../../../types/appointment";
import { useModal } from "../../../hooks/useModal";

interface Consultant {
    id: string;
    name: string;
    department: string;
}

interface TimeSlot {
    id: string;
    period: string;
    startTime: string;
    endTime: string;
}

interface AvailabilityItemUI {
    id: string;
    teacherId: string;
    day: string;
    timeSlotId: string;
    isAvailable: boolean;
}

interface SubTimeSlotAvailability {
    id: string;
    teacherId: string;
    day: string;
    timeSlotId: string;
    subTime: string;
    isAvailable: boolean;
}

const mockTimeSlots: TimeSlot[] = [
    { id: "1", period: "1. Saat", startTime: "08:00", endTime: "09:00" },
    { id: "2", period: "2. Saat", startTime: "09:00", endTime: "10:00" },
    { id: "3", period: "3. Saat", startTime: "10:00", endTime: "11:00" },
    { id: "4", period: "4. Saat", startTime: "11:00", endTime: "12:00" },
    { id: "5", period: "5. Saat", startTime: "12:00", endTime: "13:00" },
    { id: "6", period: "6. Saat", startTime: "13:00", endTime: "14:00" },
    { id: "7", period: "7. Saat", startTime: "14:00", endTime: "15:00" },
    { id: "8", period: "8. Saat", startTime: "15:00", endTime: "16:00" },
    { id: "9", period: "9. Saat", startTime: "16:00", endTime: "17:00" },
    { id: "10", period: "10. Saat", startTime: "17:00", endTime: "18:00" },
    { id: "11", period: "11. Saat", startTime: "18:00", endTime: "19:00" },
    { id: "12", period: "12. Saat", startTime: "19:00", endTime: "20:00" },
];

const days = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];

const trToEnDay: Record<string, string> = {
    Pazartesi: "monday",
    Salı: "tuesday",
    Çarşamba: "wednesday",
    Perşembe: "thursday",
    Cuma: "friday",
    Cumartesi: "saturday",
    Pazar: "sunday",
};

const enToTrDay: Record<string, string> = {
    monday: "Pazartesi",
    tuesday: "Salı",
    wednesday: "Çarşamba",
    thursday: "Perşembe",
    friday: "Cuma",
    saturday: "Cumartesi",
    sunday: "Pazar",
};

const generateSubTimeSlots = (startTime: string, stepMinutes: number): string[] => {
    const [hour, minute] = startTime.split(":").map(Number);
    const subSlots: string[] = [];
    for (let i = 0; i < 60; i += stepMinutes) {
        const mins = (minute + i) % 60;
        const hrs = hour + Math.floor((minute + i) / 60);
        subSlots.push(`${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`);
    }
    return subSlots;
};

const addMinutes = (hhmm: string, minsToAdd: number): string => {
    const [h, m] = hhmm.split(":").map(Number);
    const d = new Date(2000, 0, 1, h, m, 0, 0);
    d.setMinutes(d.getMinutes() + minsToAdd);
    const hh = d.getHours().toString().padStart(2, "0");
    const mm = d.getMinutes().toString().padStart(2, "0");
    return `${hh}:${mm}`;
};

const toMinutes = (hhmm: string): number => {
    const [h, m] = String(hhmm).split(":").map(Number);
    return h * 60 + m;
};

const findParentTimeSlotId = (started: string): string => {
    const startedMin = toMinutes(started);
    const found = mockTimeSlots.find((ts) => {
        const s = toMinutes(ts.startTime);
        const e = toMinutes(ts.endTime);
        return startedMin >= s && startedMin < e;
    });
    return found ? String(found.id) : String(started);
};

export default function ConsultantManagementTab() {
    const { showAlert } = useModal();

    const [consultants, setConsultants] = useState<Consultant[]>([]);
    const [loadingConsultants, setLoadingConsultants] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedConsultantId, setSelectedConsultantId] = useState<string | null>(null);

    const [subTimeSlotAvailability, setSubTimeSlotAvailability] = useState<SubTimeSlotAvailability[]>([]);
    const [loadingAvailability, setLoadingAvailability] = useState(false);

    const [slotDuration, setSlotDuration] = useState<number>(10);
    const [defaultCapacity, setDefaultCapacity] = useState<number>(1);

    const [modalOpen, setModalOpen] = useState(false);
    const [privateAppointmentModalOpen, setPrivateAppointmentModalOpen] = useState(false);
    const [selectedDay, setSelectedDay] = useState<string>("");
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
    const [allowedSubTimesForPrivate, setAllowedSubTimesForPrivate] = useState<string[]>([]);

    useEffect(() => {
        const load = async () => {
            setLoadingConsultants(true);
            try {
                const res = await ApiService.getAppointmentManageProviders({ limit: 100, page: 1, sortBy: "firstName", sortOrder: "ASC" });
                const results: any[] = (res as any)?.data || (res as any)?.results || (res as any)?.items || [];
                const list: Consultant[] = results.map((u: any) => ({
                    id: String(u.id),
                    name: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Danışman",
                    department: u.department?.name || "Belirtilmemiş",
                }));
                setConsultants(list);
            } catch (_) {
            } finally {
                setLoadingConsultants(false);
            }
        };
        load();
    }, []);

    const loadAvailability = useCallback(async (consultantId: string) => {
        setLoadingAvailability(true);
        setSubTimeSlotAvailability([]);
        try {
            const data = await ApiService.getAppointmentAvailability(consultantId);
            const uiSubs: SubTimeSlotAvailability[] = [];
            const weekly = (data as AppointmentAvailability)?.weekly || {};
            if (typeof (data as AppointmentAvailability)?.slotDuration === "number") {
                setSlotDuration((data as AppointmentAvailability).slotDuration);
            }
            if (typeof (data as AppointmentAvailability)?.defaultCapacity === "number") {
                setDefaultCapacity((data as AppointmentAvailability).defaultCapacity);
            }
            Object.entries(weekly).forEach(([engDay, ranges]: any) => {
                const trDay = enToTrDay[engDay] || engDay;
                Object.entries(ranges || {}).forEach(([, slots]: any) => {
                    (slots || []).forEach((slot: any) => {
                        const timeSlotId = findParentTimeSlotId(slot.started);
                        uiSubs.push({
                            id: `${engDay}-${slot.started}`,
                            teacherId: consultantId,
                            day: trDay,
                            timeSlotId: String(timeSlotId),
                            subTime: slot.started,
                            isAvailable: true,
                        });
                    });
                });
            });
            setSubTimeSlotAvailability(uiSubs);
        } catch (_) {
        } finally {
            setLoadingAvailability(false);
        }
    }, []);

    useEffect(() => {
        if (selectedConsultantId) {
            loadAvailability(selectedConsultantId);
        }
    }, [selectedConsultantId, loadAvailability]);

    const checkFullAvailability = useCallback(
        (day: string, timeSlotId: string): boolean => {
            if (!selectedConsultantId) return false;
            const slot = mockTimeSlots.find((s) => s.id === timeSlotId);
            if (!slot) return false;
            const expectedSubTimes = generateSubTimeSlots(slot.startTime, slotDuration || 10);
            const subSlots = subTimeSlotAvailability.filter(
                (s) => s.teacherId === selectedConsultantId && s.day === day && s.timeSlotId === timeSlotId
            );
            if (subSlots.length !== expectedSubTimes.length) return false;
            return subSlots.every((s) => s.isAvailable);
        },
        [selectedConsultantId, subTimeSlotAvailability, slotDuration]
    );

    const checkPartialAvailability = useCallback(
        (day: string, timeSlotId: string): boolean => {
            if (!selectedConsultantId) return false;
            const slot = mockTimeSlots.find((s) => s.id === timeSlotId);
            if (!slot) return false;
            const expectedSubTimes = generateSubTimeSlots(slot.startTime, slotDuration || 10);
            const subSlots = subTimeSlotAvailability.filter(
                (s) => s.teacherId === selectedConsultantId && s.day === day && s.timeSlotId === timeSlotId
            );
            if (subSlots.length === 0) return false;
            if (subSlots.length !== expectedSubTimes.length) {
                return subSlots.some((s) => s.isAvailable);
            }
            const availableCount = subSlots.filter((s) => s.isAvailable).length;
            return availableCount > 0 && availableCount < subSlots.length;
        },
        [selectedConsultantId, subTimeSlotAvailability, slotDuration]
    );

    const computedAvailability: AvailabilityItemUI[] = useMemo(() => {
        if (!selectedConsultantId) return [];
        return days.flatMap((day) =>
            mockTimeSlots.map((slot) => ({
                id: `${day}-${slot.id}`,
                teacherId: selectedConsultantId,
                day,
                timeSlotId: slot.id,
                isAvailable: checkFullAvailability(day, slot.id),
            }))
        );
    }, [selectedConsultantId, subTimeSlotAvailability, checkFullAvailability]);

    const getSubTimeSlotsForModal = () => {
        if (!selectedTimeSlot || !selectedConsultantId) return [];
        const subTimes = generateSubTimeSlots(selectedTimeSlot.startTime, slotDuration || 10);
        return subTimes.map((time) => {
            const existing = subTimeSlotAvailability.find(
                (s) => s.teacherId === selectedConsultantId && s.day === selectedDay && s.timeSlotId === selectedTimeSlot.id && s.subTime === time
            );
            return { time, isAvailable: existing ? existing.isAvailable : false };
        });
    };

    const handleCellClick = (day: string, timeSlotId: string) => {
        setSelectedDay(day);
        const slot = mockTimeSlots.find((s) => s.id === timeSlotId);
        setSelectedTimeSlot(slot || null);
        setModalOpen(true);
    };

    const reloadFromBackend = useCallback(async () => {
        if (!selectedConsultantId) return;
        await loadAvailability(selectedConsultantId);
    }, [selectedConsultantId, loadAvailability]);

    const handleModalConfirm = async (selectedTimes: string[]) => {
        if (!selectedTimeSlot || !selectedConsultantId) return;

        const filtered = subTimeSlotAvailability.filter(
            (s) => !(s.teacherId === selectedConsultantId && s.day === selectedDay && s.timeSlotId === (selectedTimeSlot as any).id)
        );
        const additions: SubTimeSlotAvailability[] = selectedTimes.map((time) => ({
            id: `sub-${selectedConsultantId}-${selectedDay}-${(selectedTimeSlot as any).id}-${time}`,
            teacherId: selectedConsultantId,
            day: selectedDay,
            timeSlotId: String((selectedTimeSlot as any).id),
            subTime: time,
            isAvailable: true,
        }));
        const nextSubs = [...filtered, ...additions];

        const weekly: AppointmentAvailability["weekly"] = {} as any;
        days.forEach((trDay) => {
            const enDay = trToEnDay[trDay] || trDay;
            const dayEntries: Record<string, Array<{ started: string; ended: string; capacity?: number }>> = {};
            mockTimeSlots.forEach((slot) => {
                const selectedSubs = nextSubs.filter((s) => s.day === trDay && s.timeSlotId === slot.id && s.isAvailable).map((s) => s.subTime);
                if (selectedSubs.length > 0) {
                    const rangeKey = `${slot.startTime}-${slot.endTime}`;
                    if (!dayEntries[rangeKey]) dayEntries[rangeKey] = [];
                    selectedSubs.forEach((st) => {
                        dayEntries[rangeKey].push({
                            started: st,
                            ended: addMinutes(st, slotDuration || 10),
                        });
                    });
                }
            });
            if (Object.keys(dayEntries).length > 0) {
                (weekly as any)[enDay] = dayEntries;
            }
        });

        const payload: AppointmentAvailability = {
            providerId: selectedConsultantId,
            weekly,
            defaultCapacity,
            workingHours: { start: "08:00", end: "20:00" },
            slotDuration: slotDuration || 10,
            metadata: {},
        };

        try {
            await ApiService.updateAppointmentAvailabilityForProvider(selectedConsultantId, payload);
            await reloadFromBackend();
        } catch (_) {
            await showAlert({
                title: "Hata",
                message: "Müsaitlik takvimi güncellenirken bir hata oluştu.",
                type: "error",
            });
        }
    };

    const getDateForDay = (trDay: string): string => {
        const today = new Date();
        const currentDayIndex = today.getDay();
        const dayMap: Record<string, number> = {
            Pazar: 0,
            Pazartesi: 1,
            Salı: 2,
            Çarşamba: 3,
            Perşembe: 4,
            Cuma: 5,
            Cumartesi: 6,
        };
        const targetDayIndex = dayMap[trDay] || 0;
        const daysToAdd = (targetDayIndex - currentDayIndex + 7) % 7;
        const targetDate = new Date(today);
        targetDate.setDate(today.getDate() + daysToAdd);
        const year = targetDate.getFullYear();
        const month = String(targetDate.getMonth() + 1).padStart(2, "0");
        const day = String(targetDate.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const handlePrivateAppointmentClick = async () => {
        try {
            const allowed = getSubTimeSlotsForModal()
                .filter((s) => s.isAvailable)
                .map((s) => s.time);

            if (allowed.length === 0) {
                const timeInfo = selectedTimeSlot ? `${selectedTimeSlot.period} (${selectedTimeSlot.startTime} - ${selectedTimeSlot.endTime})` : "";
                await showAlert({
                    title: "Müsait Takvim Bulunamadı",
                    message: `${selectedDay} günü için ${timeInfo} saatinde müsait takvim bulunamadı. Lütfen başka bir gün veya saat dilimi seçiniz.`,
                    type: "warning",
                });
                return;
            }

            const dateStr = getDateForDay(selectedDay);
            const res = await ApiService.getAppointments({
                providerId: selectedConsultantId!,
                startDate: dateStr,
                endDate: dateStr,
                status: "all",
                limit: 1000,
            });

            const booked = new Set<string>();
            (res?.data || []).forEach((apt: any) => {
                if (apt.status === "APPROVED" || apt.status === "PENDING") {
                    const startTime = new Date(apt.startDate).toTimeString().slice(0, 5);
                    booked.add(startTime);
                }
            });

            const filteredAllowed = allowed.filter((t) => !booked.has(t));
            if (filteredAllowed.length === 0) {
                const timeInfo = selectedTimeSlot ? `${selectedTimeSlot.period} (${selectedTimeSlot.startTime} - ${selectedTimeSlot.endTime})` : "";
                await showAlert({
                    title: "Müsait Takvim Bulunamadı",
                    message: `${selectedDay} günü için ${timeInfo} saatinde müsait takvim bulunamadı. Lütfen başka bir gün veya saat dilimi seçiniz.`,
                    type: "warning",
                });
                return;
            }

            setAllowedSubTimesForPrivate(filteredAllowed);
            setModalOpen(false);
            setPrivateAppointmentModalOpen(true);
        } catch (_) {
            const timeInfo = selectedTimeSlot ? `${selectedTimeSlot.period} (${selectedTimeSlot.startTime} - ${selectedTimeSlot.endTime})` : "";
            await showAlert({
                title: "Müsait Takvim Bulunamadı",
                message: `${selectedDay} günü için ${timeInfo} saatinde müsait takvim bulunamadı. Lütfen daha sonra tekrar deneyin.`,
                type: "warning",
            });
        }
    };

    const handlePrivateAppointmentSuccess = async () => {
        await reloadFromBackend();
    };

    const filteredConsultants = consultants.filter(
        (c) =>
            c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.department.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedConsultant = consultants.find((c) => c.id === selectedConsultantId);

    return (
        <div className="min-w-0 px-3 pb-6 pt-1 sm:px-6">
            <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-6">
                <aside className="lg:col-span-4 xl:col-span-3">
                    <div className="rounded-xl border border-slate-200/90 bg-white p-4 shadow-[0_1px_3px_rgba(15,23,42,0.06)] sm:p-5">
                        <div className="mb-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-800">Danışman seçimi</div>
                        <div className="relative mb-4">
                            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                placeholder="İsim veya birim ile ara..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full rounded-lg border border-slate-200 bg-slate-50/60 py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-500/20"
                            />
                        </div>

                        <div className="max-h-[min(24rem,55vh)] space-y-1.5 overflow-y-auto pr-0.5">
                            {loadingConsultants && (
                                <div className="flex flex-col items-center py-10 text-slate-500">
                                    <RefreshCw size={22} className="mb-2 animate-spin text-indigo-500" />
                                    <p className="text-sm">Danışmanlar yükleniyor…</p>
                                </div>
                            )}

                            {!loadingConsultants && filteredConsultants.length === 0 && (
                                <div className="flex flex-col items-center py-10 text-slate-500">
                                    <UserCircle size={32} className="mb-2 opacity-40" strokeWidth={1.25} />
                                    <p className="text-sm">Aramanızla eşleşen danışman yok.</p>
                                </div>
                            )}

                            {!loadingConsultants &&
                                filteredConsultants.map((consultant) => {
                                    const active = selectedConsultantId === consultant.id;
                                    return (
                                        <button
                                            key={consultant.id}
                                            type="button"
                                            onClick={() => setSelectedConsultantId(consultant.id)}
                                            className={`flex w-full cursor-pointer flex-col gap-0.5 rounded-lg border px-3 py-2.5 text-left transition ${
                                                active
                                                    ? "border-indigo-400/80 bg-indigo-50/80 shadow-sm ring-1 ring-indigo-500/15"
                                                    : "border-slate-200/90 bg-slate-50/40 hover:border-slate-300 hover:bg-white"
                                            }`}>
                                            <span className="flex items-center gap-2">
                                                <UserCircle
                                                    size={18}
                                                    className={`flex-shrink-0 ${active ? "text-indigo-600" : "text-slate-400"}`}
                                                    strokeWidth={1.5}
                                                />
                                                <span className={`text-sm font-medium ${active ? "text-slate-900" : "text-slate-800"}`}>
                                                    {consultant.name}
                                                </span>
                                            </span>
                                            <span className="pl-[1.625rem] text-xs text-slate-500">{consultant.department}</span>
                                        </button>
                                    );
                                })}
                        </div>
                    </div>
                </aside>

                <section className="min-w-0 lg:col-span-8 xl:col-span-9">
                    {!selectedConsultantId ? (
                        <div className="flex min-h-[min(22rem,50vh)] flex-col items-center justify-center rounded-xl border border-dashed border-slate-300/90 bg-gradient-to-b from-slate-50/80 to-white px-6 py-12 text-center">
                            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200/90 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
                                <Calendar className="h-7 w-7 text-slate-400" strokeWidth={1.25} />
                            </div>
                            <p className="text-base font-semibold text-slate-900">Önce bir danışman seçin</p>
                            <p className="mt-2 max-w-sm text-sm leading-relaxed text-slate-600">
                                Soldaki listeden danışmana tıkladığınızda, haftalık müsaitlik tablosu burada görüntülenir.
                            </p>
                        </div>
                    ) : loadingAvailability ? (
                        <div className="flex min-h-[min(22rem,50vh)] flex-col items-center justify-center rounded-xl border border-slate-200/90 bg-slate-50/50">
                            <RefreshCw size={28} className="mb-3 animate-spin text-indigo-500" />
                            <p className="text-sm font-medium text-slate-700">Takvim yükleniyor…</p>
                        </div>
                    ) : (
                        <div className="min-w-0 space-y-4">
                            <div className="grid min-w-0 w-full grid-cols-1 items-start gap-x-3 gap-y-2 border-b border-slate-200/90 pb-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 sm:gap-x-3">
                                    <h2 className="min-w-0 max-w-full truncate text-lg font-semibold tracking-tight text-slate-900 sm:text-xl">
                                        {selectedConsultant?.name}
                                    </h2>
                                    <span className="inline-flex min-w-0 max-w-full flex-wrap items-center gap-x-2 text-sm text-slate-600">
                                        <span className="inline-flex max-w-[min(100%,12rem)] shrink items-center truncate rounded-md border border-slate-200/90 bg-white px-2 py-0.5 text-xs font-medium text-slate-700">
                                            {selectedConsultant?.department}
                                        </span>
                                        <span className="shrink-0 text-slate-300">·</span>
                                        <span className="font-medium whitespace-nowrap text-slate-700">Haftalık müsaitlik</span>
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => void reloadFromBackend()}
                                    disabled={loadingAvailability}
                                    className="inline-flex shrink-0 items-center justify-center gap-1.5 justify-self-start rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-50 sm:justify-self-end">
                                    <RefreshCw size={14} className={loadingAvailability ? "animate-spin" : ""} />
                                    Yenile
                                </button>
                            </div>
                            <WeeklyAvailabilityGrid
                                refined
                                days={days}
                                timeSlots={mockTimeSlots}
                                availability={computedAvailability}
                                selectedEntityId={selectedConsultantId}
                                getEntityId={(item) => (item as AvailabilityItemUI).teacherId}
                                getIsAvailable={(item) => (item as AvailabilityItemUI).isAvailable}
                                getDay={(item) => (item as AvailabilityItemUI).day}
                                getTimeSlotId={(item) => (item as AvailabilityItemUI).timeSlotId}
                                onToggle={handleCellClick}
                                getPartialAvailability={checkPartialAvailability}
                                onCellClick={handleCellClick}
                                disabled={false}
                                showLegend={true}
                            />
                        </div>
                    )}
                </section>
            </div>

            <TimeSlotDetailModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                day={selectedDay}
                timeSlot={selectedTimeSlot || { period: "", startTime: "", endTime: "" }}
                subTimeSlots={getSubTimeSlotsForModal()}
                disabled={false}
                readOnly={false}
                onPrivateAppointment={handlePrivateAppointmentClick}
                onConfirm={handleModalConfirm}
            />

            <PrivateAppointmentModal
                isOpen={privateAppointmentModalOpen}
                onClose={() => setPrivateAppointmentModalOpen(false)}
                day={selectedDay}
                date={getDateForDay(selectedDay)}
                timeSlot={selectedTimeSlot || { period: "", startTime: "", endTime: "" }}
                slotDuration={slotDuration}
                onSuccess={handlePrivateAppointmentSuccess}
                allowedSubTimes={allowedSubTimesForPrivate}
                providerId={selectedConsultantId || undefined}
            />
        </div>
    );
}
